import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getSignDocumentUrl,
  getSignInstance,
  resendSignInstance,
  voidSignInstance,
  type ContractInstance,
  type ContractSigner,
} from "@/lib/sign/signApiClient";
import { SignInstanceStatusChip, SignSignerStatusChip } from "./SignInstanceStatusChip";

const VOID_ROLES = new Set([
  "trainer",
  "marketing",
  "admin",
  "owner",
  "dev",
  "platform_owner",
  "platform_admin",
  "platform_dev",
  "platform_trainer",
]);

const RESENDABLE_INSTANCE = new Set(["sent", "viewed", "partially_signed"]);
const RESENDABLE_SIGNER = new Set(["pending", "sent", "viewed"]);
const VOIDABLE_INSTANCE = new Set([
  "draft",
  "sent",
  "viewed",
  "partially_signed",
  "declined",
  "expired",
]);

type Props = {
  instanceId: string;
  /** Called after resend/void succeeds so parent can refresh related state. */
  onMutated?: () => void;
};

export function SignInstancePanel({ instanceId, onMutated }: Props) {
  const auth = useAuth();
  const [instance, setInstance] = useState<ContractInstance | null>(null);
  const [signers, setSigners] = useState<ContractSigner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busySignerId, setBusySignerId] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidBusy, setVoidBusy] = useState(false);
  const [docBusy, setDocBusy] = useState(false);

  const canVoid = auth.hasRole(...Array.from(VOID_ROLES));

  const reload = useCallback(async () => {
    if (!instanceId.trim()) {
      setInstance(null);
      setSigners([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const r = await getSignInstance(instanceId.trim());
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      setInstance(null);
      setSigners([]);
      return;
    }
    setInstance(r.data.instance);
    setSigners(r.data.signers);
  }, [instanceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleResend(signer: ContractSigner) {
    if (!instance) return;
    setBusySignerId(signer.id);
    setError(null);
    const r = await resendSignInstance({ instance_id: instance.id, signer_id: signer.id });
    setBusySignerId(null);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setInstance(r.data.instance);
    setSigners(r.data.signers);
    onMutated?.();
  }

  async function handleVoid() {
    if (!instance || !voidReason.trim()) return;
    setVoidBusy(true);
    setError(null);
    const r = await voidSignInstance({ instance_id: instance.id, reason: voidReason.trim() });
    setVoidBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setVoidOpen(false);
    setVoidReason("");
    setInstance(r.data.instance);
    setSigners(r.data.signers);
    onMutated?.();
  }

  async function handleOpenSignedPdf() {
    if (!instance) return;
    setDocBusy(true);
    setError(null);
    const r = await getSignDocumentUrl(instance.id);
    setDocBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    window.open(r.data.signed_url, "_blank", "noopener,noreferrer");
  }

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">Loading contract instance…</p>;
  }

  if (!instance) {
    return error ? (
      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
        {error}
      </div>
    ) : null;
  }

  const showVoid = canVoid && VOIDABLE_INSTANCE.has(instance.status);
  const showResend = RESENDABLE_INSTANCE.has(instance.status);

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Contract signing</h3>
            <SignInstanceStatusChip status={instance.status} />
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
            Instance {instance.id.slice(0, 8)}…
            {instance.envelope_id ? ` · envelope ${instance.envelope_id.slice(0, 8)}…` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {instance.signed_pdf_storage_path ? (
            <button
              type="button"
              disabled={docBusy}
              onClick={() => void handleOpenSignedPdf()}
              className="rounded-md border border-emerald-300 px-2 py-1 text-xs font-medium text-emerald-800 dark:border-emerald-800 dark:text-emerald-200"
            >
              {docBusy ? "Opening…" : "View signed PDF"}
            </button>
          ) : null}
          {showVoid ? (
            <button
              type="button"
              onClick={() => setVoidOpen((v) => !v)}
              className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium text-red-800 dark:border-red-800 dark:text-red-200"
            >
              Void envelope
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {error}
        </div>
      ) : null}

      {voidOpen && showVoid ? (
        <div className="space-y-2 rounded-lg border border-red-200 bg-red-50/50 p-3 dark:border-red-900 dark:bg-red-950/20">
          <label className="block text-xs text-gray-700 dark:text-zinc-300">
            Reason for voiding (required)
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={voidBusy || !voidReason.trim()}
              onClick={() => void handleVoid()}
              className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {voidBusy ? "Voiding…" : "Confirm void"}
            </button>
            <button
              type="button"
              onClick={() => {
                setVoidOpen(false);
                setVoidReason("");
              }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs dark:border-zinc-600"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100 dark:divide-zinc-800 dark:border-zinc-800">
        {signers.length === 0 ? (
          <li className="px-3 py-4 text-sm text-gray-500 dark:text-zinc-400">No signers on this instance.</li>
        ) : (
          signers.map((signer) => {
            const contact =
              signer.channel === "sms"
                ? signer.phone
                : signer.email ?? signer.phone ?? "—";
            const canResend =
              showResend && RESENDABLE_SIGNER.has(signer.status) && Boolean(signer.provider_signer_id);

            return (
              <li key={signer.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium capitalize text-gray-900 dark:text-zinc-100">
                      {signer.role.replace(/_/g, " ")}
                    </span>
                    <SignSignerStatusChip status={signer.status} />
                    <span className="text-[10px] uppercase tracking-wide text-gray-400">{signer.channel}</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-zinc-400">{contact}</div>
                </div>
                {canResend ? (
                  <button
                    type="button"
                    disabled={busySignerId === signer.id}
                    onClick={() => void handleResend(signer)}
                    className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium dark:border-zinc-600"
                  >
                    {busySignerId === signer.id ? "Sending…" : "Resend"}
                  </button>
                ) : null}
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
